import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SnapshotWidget } from '@vendasta/snapshot-widget';
import {
  Cell,
  ColumnDescriptor,
  MenuItem,
  SwitchCell,
  TableViewConfig,
  TableViewModel,
  UIKitModule,
  VaMenuComponent,
  VaMultilineComponent,
} from '@vendasta/uikit';
import { combineLatest, Observable, skipWhile, Subscription, take } from 'rxjs';
import { AppConfigService } from '../app-config.service';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { getImageSrc } from '../core/images';
import { Market } from '../core/markets/market';
import { MarketsService } from '../core/markets/markets.service';
import { AcquisitionWidgetStoreService } from './acquisition-widget-store.service';

interface DialogData {
  title: string;
  body: string;
  buttonText: string;
  buttonClass: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>{{ dialogData.title }}</h2>
    <mat-dialog-content>
      <p [innerHTML]="dialogData.body"></p>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-stroked-button (click)="dialogRef.close(false)">Cancel</button>
      <button mat-flat-button [ngClass]="dialogData.buttonClass" (click)="dialogRef.close(true)">
        {{ dialogData.buttonText }}
      </button>
    </mat-dialog-actions>
  `,
  styleUrls: ['./acquisition-widget-overview.component.scss'],
})
export class ConfirmationDialogComponent {
  @Input() dialogData: DialogData;

  constructor(public dialogRef: MatDialogRef<ConfirmationDialogComponent>) {}
}

@Component({
  standalone: true,
  imports: [
    GalaxyPageModule,
    UIKitModule,
    GalaxyEmptyStateModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyFormFieldModule,
    MatButtonModule,
    MatSelectModule,
  ],
  templateUrl: './acquisition-widget-overview.component.html',
  styleUrls: ['./acquisition-widget-overview.component.scss'],
})
export class AcquisitionWidgetOverviewComponent implements OnInit, OnDestroy {
  hasMore$: Observable<boolean>;
  loadingMore$: Observable<boolean>;
  widgets$: Observable<SnapshotWidget[]>;
  hasMarketsAccess$: Observable<boolean>;
  markets: Market[] = [];
  allMarketsWidgets$: Observable<SnapshotWidget[]>;
  subscriptions_: Subscription[] = [];
  getImageSrc = getImageSrc;

  columnConfigs: ColumnDescriptor<SnapshotWidget>[] = [
    {
      title: this.translate.instant('ACQUISITION_WIDGETS.TABLE.WIDGET'),
      cellForItem: this.widgetNameCell.bind(this),
      config: {
        width: '280px',
      },
      alignment: 'left',
    },
    {
      title: this.translate.instant('ACQUISITION_WIDGETS.TABLE.SALESPERSON'),
      cellForItem: this.salesPersonCell.bind(this),
      config: {
        width: '100px',
      },
      alignment: 'left',
    },
    {
      title: '',
      cellForItem: this.toggleCell.bind(this),
      config: {
        width: '50px',
      },
    },
    {
      title: '',
      cellForItem: this.menuCell.bind(this),
      config: {
        width: '50px',
      },
    },
  ];

  marketColumnDescriptor: ColumnDescriptor<SnapshotWidget> = {
    title: this.translate.instant('ACQUISITION_WIDGETS.TABLE.MARKET'),
    cellForItem: this.marketCell.bind(this),
    config: {
      width: '280px',
    },
    alignment: 'left',
  };

  acquisitionWidgetTable: TableViewModel<SnapshotWidget> = new TableViewModel<SnapshotWidget>({
    columns: this.columnConfigs,
    emptyText: this.translate.instant('ACQUISITION_WIDGETS.TABLE.EMPTY'),
    loadMoreItemsTriggered: this.loadMore.bind(this),
    fixedTable: true,
  } as TableViewConfig<SnapshotWidget>);

  constructor(
    private marketsService: MarketsService,
    private configService: AppConfigService,
    private acquisitionWidgetsStoreService: AcquisitionWidgetStoreService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private router: Router,
    private title: Title,
    @Inject(StubAccessService) private accessService: AccessService,
    private readonly translate: TranslateService,
  ) {}

  private widgetNameCell(widget: SnapshotWidget): Cell {
    return {
      config: {
        title: widget.name,
        link: {
          url: `/marketing/widget/wizard/`,
          queryParams: { widgetId: widget.widgetId },
        },
      },
      component: VaMultilineComponent,
    };
  }

  private marketCell(widget: SnapshotWidget): Cell {
    const market = this.markets.find((m) => m.market_id === widget.marketId);
    return {
      config: {
        title: market ? market.name : this.translate.instant('ACQUISITION_WIDGETS.TABLE.UNKNOWN_MARKET'),
      },
      component: VaMultilineComponent,
    };
  }

  private salesPersonCell(widget: SnapshotWidget): Cell {
    return {
      config: {
        title: this.acquisitionWidgetsStoreService.getSalespeople(widget.salespersonIds).displayName,
        link: '',
      },
      component: VaMultilineComponent,
    };
  }

  private toggleCell(widget: SnapshotWidget): SwitchCell {
    return new SwitchCell({
      disabled: false,
      checked: !widget.disabled,
      onClick: (checked) => this.handleEnableToggle(widget, checked),
    });
  }

  private menuCell(widget: SnapshotWidget): Cell {
    const actions: MenuItem[] = [
      {
        text: this.translate.instant('ACQUISITION_WIDGETS.TABLE.EDIT'),
        click: () => {
          this.router.navigate(['/marketing/widget/wizard'], {
            queryParams: { widgetId: widget.widgetId },
          });
        },
        disabled: false,
      },
      {
        text: this.translate.instant('ACQUISITION_WIDGETS.TABLE.DELETE'),
        click: () => {
          const deleteDialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '620px',
          });
          deleteDialogRef.componentInstance.dialogData = {
            title: this.translate.instant('ACQUISITION_WIDGETS.TABLE.DELETE_MODAL.TITLE'),
            body:
              this.translate.instant('ACQUISITION_WIDGETS.TABLE.DELETE_MODAL.DESCRIPTION_START', {
                name: widget.name,
              }) +
              '<br/><br/>' +
              this.translate.instant('ACQUISITION_WIDGETS.TABLE.DELETE_MODAL.DESCRIPTION_END'),
            buttonText: this.translate.instant('ACQUISITION_WIDGETS.TABLE.DELETE'),
            buttonClass: 'button-warning',
          };
          this.subscriptions_.push(
            deleteDialogRef.afterClosed().subscribe((deleteFlag) => {
              if (deleteFlag) {
                this.acquisitionWidgetsStoreService.deleteWidget(widget);
              }
            }),
          );
        },
        disabled: false,
      },
    ];

    return {
      config: {
        menuItems: actions,
      },
      component: VaMenuComponent,
    };
  }

  ngOnInit(): void {
    // Always show all markets on page load
    this.changeMarket(null);
    this.widgets$ = this.acquisitionWidgetsStoreService.widgets$;
    this.allMarketsWidgets$ = this.widgets$.pipe(skipWhile((w) => !w));
    this.hasMore$ = this.acquisitionWidgetsStoreService.hasMore$;
    this.loadingMore$ = this.acquisitionWidgetsStoreService.loadingMore$;
    this.subscriptions_.push(
      combineLatest([this.marketsService.userAccessibleMarkets$, this.configService.userAccessibleMarkets$]).subscribe(
        ([markets, marketIds]) => {
          const marketsWithoutNull = markets.filter((m) => m.market_id !== null);
          if (!marketIds || marketIds.length === 0) {
            const allMarkets = new Market({ name: 'All Markets', market_id: null });
            this.markets = [allMarkets].concat(marketsWithoutNull);
          } else {
            this.markets = marketsWithoutNull;
          }
        },
      ),
    );
    this.acquisitionWidgetsStoreService.initializeStore();
    this.hasMarketsAccess$ = this.accessService.hasAccessToFeature(Feature.markets);
    this.subscriptions_.push(
      this.acquisitionWidgetsStoreService.notification$.subscribe((notification) => {
        if (!notification) {
          return;
        }
        if (notification.error) {
          this.snackbarService.errorSnack(notification.text);
        } else {
          this.snackbarService.successSnack(notification.text);
        }
      }),
      this.hasMarketsAccess$.subscribe((marketsAccess) => {
        if (marketsAccess) {
          this.acquisitionWidgetTable.addColumnAtIndex(this.marketColumnDescriptor, 1);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions_.forEach((sub) => sub.unsubscribe);
    this.acquisitionWidgetsStoreService.destroyStore();
  }

  loadMore(): void {
    this.acquisitionWidgetsStoreService.loadMoreWidgets();
  }

  changeMarket(marketId: string | null): void {
    // For users that do not have access to all markets we must pick their first market if the market id is null
    // The acquisition widget currently does not support null markets for users in specific markets.
    combineLatest([this.configService.userAccessibleMarkets$, this.marketsService.userAccessibleMarkets$])
      .pipe(take(1))
      .subscribe(([userMarketIds, accessibleMarkets]) => {
        this.acquisitionWidgetsStoreService.selectMarket(
          !marketId && !!userMarketIds && userMarketIds.length > 0 ? accessibleMarkets[0].market_id : marketId,
        );
      });
  }

  navigateToURL(url: string): void {
    this.router.navigateByUrl(url);
  }

  private handleEnableToggle(widget: SnapshotWidget, checked: boolean): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '480px',
    });
    if (checked) {
      dialogRef.componentInstance.dialogData = {
        title: this.translate.instant('ACQUISITION_WIDGETS.TABLE.ENABLE_MODAL.TITLE', { name: widget.name }),
        body: this.translate.instant('ACQUISITION_WIDGETS.TABLE.ENABLE_MODAL.DESCRIPTION'),
        buttonText: this.translate.instant('ACQUISITION_WIDGETS.TABLE.ENABLE_MODAL.BUTTON'),
        buttonClass: 'button-normal',
      };
    } else {
      dialogRef.componentInstance.dialogData = {
        title: this.translate.instant('ACQUISITION_WIDGETS.TABLE.DISABLE_MODAL.TITLE', { name: widget.name }),
        body: this.translate.instant('ACQUISITION_WIDGETS.TABLE.DISABLE_MODAL.DESCRIPTION'),
        buttonText: this.translate.instant('ACQUISITION_WIDGETS.TABLE.DISABLE_MODAL.BUTTON'),
        buttonClass: 'button-warning',
      };
    }
    this.subscriptions_.push(
      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          widget.disabled = !checked;
          this.acquisitionWidgetsStoreService.updateWidget(widget);
        }
        this.acquisitionWidgetTable.updateColumnAtIndex(3, {
          cellForItem: this.toggleCell.bind(this),
        });
      }),
    );
  }
}
